import {
  Day,
  differenceInMinutes as dateDifferenceInMinutes,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  getQuarter as _getQuarter,
  isPast as _isPast,
  isSameWeek as _isSameWeek,
  isToday as _isToday,
  isTomorrow as _isTomorrow,
  isYesterday as _isYesterday,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';
import { MONTHS } from '@flemingmetrics/api-client';
import { DDMM, MMDD } from '@/constants/dateFormatPreference';
import { DateRange } from '@/types/dates';
import { isNumber, padNumber } from '@/utils/numbers';

export function parseDate(ISODateString: string): Date {
  // Convert an ISO date string to a Date object
  return parseISO(ISODateString);
}

export function buildYearsArray(start: number, end: number, descending = true) {
  let result = [];
  const minYear = Math.min(start, end);
  const maxYear = Math.max(start, end);
  for (let i = maxYear; i >= minYear; i--) {
    result.push(i);
  }
  return descending ? result : result.reverse();
}

export function buildHoursArray(is24Hour = false) {
  let start = is24Hour ? 0 : 1;
  let end = is24Hour ? 24 : 13;
  let result = [];
  for (let i = start; i < end; i++) {
    result.push({ value: i, label: padNumber(i) });
  }
  return result;
}

export function buildMinutesArray(interval = 1) {
  let result = [];
  for (let i = 0; i <= 59; i += interval) {
    result.push({ value: i, label: padNumber(i) });
  }
  return result;
}

export const isToday = (ISODateString: string) =>
  _isToday(parseDate(ISODateString));

export const isTomorrow = (ISODateString: string) =>
  _isTomorrow(parseDate(ISODateString));

export const isYesterday = (ISODateString: string) =>
  _isYesterday(parseDate(ISODateString));

export const isThisWeek = (ISODateString: string, weekStartsOn: Day = 0) =>
  _isSameWeek(parseDate(ISODateString), new Date(), { weekStartsOn });

export const isPast = (ISODateString: string) =>
  _isPast(parseDate(ISODateString));

export function renderDate(ISODateString: string, dateFormat = MMDD) {
  const formatStr = dateFormat === DDMM ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
  return format(parseDate(ISODateString), formatStr);
}

export function renderTextDate(ISODateString: string, dateFormat = MMDD) {
  const formatStr = dateFormat === DDMM ? 'dd MMM yyyy' : 'MMM dd yyyy';
  return format(parseDate(ISODateString), formatStr);
}

export function renderDateWithTime(ISODateString: string, dateFormat = MMDD) {
  const formatStr = `${
    dateFormat === DDMM ? 'dd MMM yyyy' : 'MMM dd yyyy'
  }, hh:mma`;
  return format(parseDate(ISODateString), formatStr);
}

export function renderDateOnlyTime(ISODateString: string) {
  return format(parseDate(ISODateString), 'hh:mma');
}

export function renderDayOfWeek(ISODateString: string, fullName = true) {
  return format(parseDate(ISODateString), fullName ? 'iiii' : 'iii');
}

export function renderRelativeDate(
  ISODateString: string,
  options?: {
    todayAsTime?: boolean;
    includeTime?: boolean;
    dateFormat?: string | null;
  }
) {
  const todayAsTime = options?.todayAsTime || false;
  const includeTime = options?.todayAsTime || false;
  const dateFormat = options?.dateFormat || MMDD;
  if (isToday(ISODateString)) {
    return todayAsTime ? renderDateOnlyTime(ISODateString) : 'Today';
  } else if (isTomorrow(ISODateString)) {
    return 'Tomorrow';
  } else if (isYesterday(ISODateString)) {
    return 'Yesterday';
  }
  return includeTime
    ? renderDateWithTime(ISODateString, dateFormat)
    : renderTextDate(ISODateString, dateFormat);
}

export function buildDuration(startsAt: string, endsAt: string) {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const minutes = (end.valueOf() - start.valueOf()) / (60 * 1000);
  return {
    hours: (minutes - (minutes % 60)) / 60,
    minutes: minutes % 60,
  };
}

export function getTimeOfDayMs() {
  let now = Date.now();
  let midnight = new Date();
  midnight.setHours(0, 0, 0);
  return now - midnight.valueOf();
}

export function getQuarter(ISODateString: string) {
  return _getQuarter(parseDate(ISODateString));
}

// Timeframe methods

function formatTimeframe(start: Date, end: Date): DateRange {
  return [start.toISOString(), end.toISOString()];
}

function formatEndDate(endDate?: Date) {
  // Defaults to today if no date is provided
  return endDate ? new Date(endDate) : new Date();
}

export function getStartOfDay(ISODateString: string | number) {
  return startOfDay(new Date(ISODateString)).toISOString();
}

export function getEndOfDay(ISODateString: string | number) {
  return endOfDay(new Date(ISODateString)).toISOString();
}

export function getFullDay(ISODateString: string | number) {
  const date = new Date(ISODateString);
  return formatTimeframe(startOfDay(date), endOfDay(date));
}

export function getToday() {
  const date = new Date();
  return formatTimeframe(startOfDay(date), endOfDay(date));
}

export function getYesterday() {
  const date = subDays(new Date(), 1);
  return formatTimeframe(startOfDay(date), endOfDay(date));
}

export function getThisWeek(weekStartsOn: Day = 0) {
  const date = new Date();
  return formatTimeframe(
    startOfWeek(date, { weekStartsOn }),
    endOfWeek(date, { weekStartsOn })
  );
}

export function getLastWeek(weekStartsOn: Day = 0) {
  const date = subWeeks(new Date(), 1);
  return formatTimeframe(
    startOfWeek(date, { weekStartsOn }),
    endOfWeek(date, { weekStartsOn })
  );
}

export function getPastSevenDays(endDate?: Date) {
  const date = formatEndDate(endDate);
  return formatTimeframe(startOfDay(subDays(date, 7)), endOfDay(date));
}

export function getMonthToDate(endDate?: Date) {
  const date = formatEndDate(endDate);
  return formatTimeframe(startOfMonth(date), endOfDay(date));
}

export function getLastMonth(endDate?: Date) {
  const date = subMonths(formatEndDate(endDate), 1);
  return formatTimeframe(startOfMonth(date), endOfMonth(date));
}

export function getPastThirtyDays(endDate?: Date) {
  const date = formatEndDate(endDate);
  return formatTimeframe(startOfDay(subDays(date, 30)), endOfDay(date));
}

export function getPastNinetyDays(endDate?: Date) {
  const date = formatEndDate(endDate);
  return formatTimeframe(startOfDay(subDays(date, 90)), endOfDay(date));
}

export function getYearToDate(endDate?: Date) {
  const date = formatEndDate(endDate);
  return formatTimeframe(startOfYear(date), endOfDay(date));
}

export function getLastYear(endDate?: Date) {
  const date = subYears(formatEndDate(endDate), 1);
  return formatTimeframe(startOfYear(date), endOfYear(date));
}

export function differenceInMinutes(
  startISODateString: string,
  endISODateString: string
) {
  return dateDifferenceInMinutes(
    parseDate(endISODateString),
    parseDate(startISODateString)
  );
}

export function isDateColumn(col: string): col is 'createdAt' | 'updatedAt' {
  return ['createdAt', 'updatedAt'].includes(col);
}

export function formatMonth(month?: number) {
  if (!isNumber(month)) return '--';
  const name = MONTHS[month]?.short;
  return name || '--';
}

export function formatQuarter(quarter?: number) {
  return isNumber(quarter) ? `Q${quarter}` : '--';
}

export function formatDateOnlyTime(ISODateString: string): string {
  return format(parseDate(ISODateString), 'hh:mma');
}

export function formatDateWithTime(
  ISODateString: string,
  dateFormat = MMDD
): string {
  const formatStr = `${
    dateFormat === DDMM ? 'dd MMM yyyy' : 'MMM dd yyyy'
  }, hh:mma`;
  return format(parseDate(ISODateString), formatStr);
}

export function formatTextDate(
  ISODateString: string,
  dateFormat = MMDD
): string {
  const formatStr = dateFormat === DDMM ? 'dd MMM yyyy' : 'MMM dd yyyy';
  return format(parseDate(ISODateString), formatStr);
}

export function formatRelativeDate(
  ISODateString: string,
  options?: {
    todayAsTime?: boolean;
    includeTime?: boolean;
    dateFormat?: string | null;
  }
): string {
  const todayAsTime = options?.todayAsTime || false;
  const includeTime = options?.todayAsTime || false;
  const dateFormat = options?.dateFormat || MMDD;
  if (isToday(ISODateString)) {
    return todayAsTime ? formatDateOnlyTime(ISODateString) : 'Today';
  } else if (isTomorrow(ISODateString)) {
    return 'Tomorrow';
  } else if (isYesterday(ISODateString)) {
    return 'Yesterday';
  }
  return includeTime
    ? formatDateWithTime(ISODateString, dateFormat)
    : formatTextDate(ISODateString, dateFormat);
}
